import { useTranslation } from 'react-i18next';

import Tooltip from '@/components/atoms/Tooltip';
import { Menu, MenuContent, MenuHeader, MenuItem, MenuTrigger } from '@/components/molecules/Menu';

import { rowWeightLabel } from '../../helpers/weightText';
import type { GroupRowModel } from '../../types';

import styles from './GroupLabel.module.scss';

interface ChainMenuProps {
    row: GroupRowModel;
    /** Opens an earlier group's dialog; without it the entries only list them. */
    onOpenMember?: (groupId: number) => void;
}

/**
 * The "N groups" chip of a chain row: the earlier groups drawn on the row, newest first, with
 * the weight each ended at. It is also the keyboard path to their dialogs, which a pointer
 * reaches by clicking that group's part of the bar.
 */
export default function ChainMenu(props: ChainMenuProps) {
    const { row, onOpenMember } = props;
    const { t, i18n } = useTranslation('fishGroups');
    const count = row.members.length;
    const earlier = row.members.slice(0, -1).reverse();

    return (
        <Menu>
            <MenuTrigger
                variant="unstyled"
                className={styles.chainToggle}
                aria-label={t('board.row.chainLabel', { count })}>
                {t('board.row.chain', { count })}
            </MenuTrigger>
            {/* Out of the board: the sticky label cells of the rows below would cover it. */}
            <MenuContent width="wide" container="body">
                <MenuHeader>{t('board.row.chainEarlier')}</MenuHeader>
                {earlier.map((member) => {
                    const weight = rowWeightLabel(
                        member.weight,
                        member.endedKind ? t(`board.ended.${member.endedKind}`) : null,
                        i18n.language,
                        t
                    );
                    // The tooltip wraps the item the arrow keys focus, so it opens on focus as on
                    // hover; its text is also in the item's name for screen readers.
                    return (
                        <Tooltip
                            key={member.groupId}
                            content={weight.title}
                            layout="fill"
                            placement="right">
                            <MenuItem
                                disabled={!onOpenMember}
                                onClick={() => onOpenMember?.(member.groupId)}>
                                <span className={styles.chainMember}>
                                    <span>{member.name}</span>
                                    <span className={styles.chainMemberWeight}>{weight.text}</span>
                                    <span className={styles.visuallyHidden}>{weight.title}</span>
                                </span>
                            </MenuItem>
                        </Tooltip>
                    );
                })}
            </MenuContent>
        </Menu>
    );
}
